import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { AuthData } from '../zero/auth.ts'

export interface Session {
  sessionId: string
  token: string
  auth: AuthData
  expiresAt: number
  accessTokenExpiresAt: number
}

export type LoginResult =
  | { status: 'ok' }
  | { status: 'incorrect'; message: string }
  | { status: 'pending' }
  | { status: 'locked'; lockedUntil: number }
  | { status: 'error'; message: string }

interface AuthContextValue {
  status: 'loading' | 'ready'
  session: Session | null
  login: (pin: string) => Promise<LoginResult>
  logout: () => Promise<void>
  refreshSession: () => Promise<boolean>
}

interface ErrorBody {
  code?: string
  message?: string
  lockedUntil?: number
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function readSessionResponse(res: Response): Promise<Session | null> {
  if (!res.ok) return null
  return (await res.json()) as Session
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'ready'>('loading')
  const [session, setSession] = useState<Session | null>(null)

  const refreshSession = useCallback(async () => {
    try {
      const res = await fetch('/api/session', { credentials: 'include' })
      if (res.status === 401) {
        setSession(null)
        return false
      }
      const next = await readSessionResponse(res)
      if (next) setSession(next)
      return next != null
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    localStorage.removeItem('sync.session')
    void refreshSession().finally(() => setStatus('ready'))
  }, [refreshSession])

  useEffect(() => {
    if (!session) return
    const refreshIn = Math.max(1_000, session.accessTokenExpiresAt - Date.now() - 60_000)
    const timeout = window.setTimeout(() => void refreshSession(), refreshIn)
    return () => window.clearTimeout(timeout)
  }, [refreshSession, session])

  useEffect(() => {
    function refreshOnFocus() {
      if (document.visibilityState === 'visible' && session) void refreshSession()
    }
    document.addEventListener('visibilitychange', refreshOnFocus)
    return () => document.removeEventListener('visibilitychange', refreshOnFocus)
  }, [refreshSession, session])

  const login = useCallback(async (pin: string): Promise<LoginResult> => {
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pin }),
      })
      if (res.ok) {
        const next = (await res.json()) as Session
        setSession(next)
        return { status: 'ok' }
      }

      const body = (await res.json().catch(() => ({}))) as ErrorBody
      if (body.code === 'PENDING_APPROVAL') return { status: 'pending' }
      if (body.code === 'PIN_LOCKED' && typeof body.lockedUntil === 'number') {
        return { status: 'locked', lockedUntil: body.lockedUntil }
      }
      if (body.code === 'INCORRECT_PIN') {
        return { status: 'incorrect', message: 'Incorrect PIN' }
      }
      return { status: 'error', message: body.message ?? 'Login failed' }
    } catch {
      return { status: 'error', message: 'Unable to reach the server' }
    }
  }, [])

  const logout = useCallback(async () => {
    setSession(null)
    try {
      await fetch('/api/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // The local session is still cleared; the cookie expires server-side.
    }
  }, [])

  const value = useMemo(
    () => ({ status, session, login, logout, refreshSession }),
    [status, session, login, logout, refreshSession],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
